import type { InventoryBundleSearchInput, InventoryDomainSearchInput } from '@openx/types/gqlTypes.ts';

type UnknownRecord = Record<string, unknown>;

const isUnsetValue = (value: unknown): boolean =>
  value === null || value === undefined || (Array.isArray(value) && value.length === 0);

const isPrimitiveValue = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

const isPlainObjectValue = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasPrimitiveArrayDifferenceAsSet = (userArray: unknown[], wisspArray: unknown[]): boolean => {
  const userValues = userArray as (string | number | boolean)[];
  const wisspValues = wisspArray as (string | number | boolean)[];

  if (userValues.length !== wisspValues.length) {
    return true;
  }

  const sortedUserValues = [...userValues].sort();
  const sortedWisspValues = [...wisspValues].sort();

  for (let index = 0; index < sortedUserValues.length; index++) {
    if (sortedUserValues[index] !== sortedWisspValues[index]) {
      return true;
    }
  }

  return false;
};

const hasDifferenceRespectingUnsetValues = (userValue: unknown, wisspValue: unknown): boolean => {
  if (isUnsetValue(userValue)) {
    return false;
  }

  if (Array.isArray(userValue)) {
    if (!Array.isArray(wisspValue)) {
      return true;
    }

    if (userValue.every(isPrimitiveValue) && wisspValue.every(isPrimitiveValue)) {
      return hasPrimitiveArrayDifferenceAsSet(userValue, wisspValue);
    }

    if (userValue.length !== wisspValue.length) {
      return true;
    }

    for (let index = 0; index < userValue.length; index++) {
      if (hasDifferenceRespectingUnsetValues(userValue[index], wisspValue[index])) {
        return true;
      }
    }

    return false;
  }

  if (isPrimitiveValue(userValue)) {
    return userValue !== wisspValue;
  }

  if (isPlainObjectValue(userValue)) {
    if (!isPlainObjectValue(wisspValue)) {
      return true;
    }

    const userObject = userValue;
    const wisspObject = wisspValue;

    for (const key of Object.keys(userObject)) {
      if (hasDifferenceRespectingUnsetValues(userObject[key], wisspObject[key])) {
        return true;
      }
    }

    return false;
  }

  return userValue !== wisspValue;
};

export type InventoryInput = InventoryBundleSearchInput | InventoryDomainSearchInput;

export const areFiltersDifferent = (userInput: InventoryInput, wisspInput: InventoryInput): boolean => {
  return hasDifferenceRespectingUnsetValues(userInput.filters.domainFilters, wisspInput.filters.domainFilters);
};
